<template>
  <div class="custom-calendar">
    <div class="calendar-grid" :class="{ 'with-weekly-summary': showWeeklySummary }">
      <div class="day-name" v-for="day in dayNames" :key="day">
        {{ day }}
      </div>
      <div v-if="showWeeklySummary" class="day-name weekly-summary-header">
        Week Total
      </div>
      <template v-for="(date, idx) in daysInMonth">
        <div
          :key="`day-${idx}`"
          class="calendar-day"
          :class="{
            'other-month': date.getMonth() !== currentMonth.getMonth(),
            'out-of-range': !isInCustomRange(date),
            today: isToday(date),
          }"
        >
          <div class="date-number">{{ date.getDate() }}</div>
          <!-- Work Schedule Indicator - Show unless it's a full day off -->
          <div v-if="getScheduledHours && getScheduledHours(date) && !isFullDayOff(date)" class="work-schedule-container">
            <div
              v-for="(schedule, index) in Array.isArray(getScheduledHours(date)) ? getScheduledHours(date) : [getScheduledHours(date)]"
              :key="index"
              class="work-schedule-indicator"
            >
              <i class="fas fa-clock"></i>
              <span>{{ formatTime(schedule.start) }} - {{ formatTime(schedule.end) }}</span>
            </div>
          </div>
          <!-- Approved Time Off Indicator - Show below work schedule for partial days -->
          <div v-if="isDateOnTimeOff(date)" class="time-off-indicator" :title="getTimeOffTooltip(isDateOnTimeOff(date))">
            <i class="fas fa-umbrella-beach"></i>
            <span>{{ formatTimeOffDisplay(isDateOnTimeOff(date)) }}</span>
          </div>
          <el-tooltip
            v-for="shift in eventsForDate(date)"
            :key="shift.id"
            placement="bottom"
            effect="light"
            :open-delay="300"
          >
            <div slot="content" v-html="formatShiftTooltipHtml(shift)"></div>
            <div
              class="shift-item"
              :class="[
                shift.statusClass,
                getPayerClass(shift),
                getShiftTypeClass(shift),
              ]"
              @click.stop="$emit('show-details', shift)"
            >
              <span class="shift-time">
                {{ shift.formattedStartTime }}
              </span>
              <span class="shift-client">{{ shift.groupName || shift.client }}</span>
              <span class="shift-caregiver" v-if="!shift.groupName && shift.caregiver && shift.caregiver !== 'Unassigned'">{{ shift.caregiver }}</span>
            </div>
          </el-tooltip>
        </div>
        <!-- Weekly Summary Cell - appears after every 7th day (Saturday) -->
        <div v-if="showWeeklySummary && (idx + 1) % 7 === 0" :key="`summary-${idx}`" class="calendar-day weekly-summary-cell">
          <div class="weekly-summary-content">
            <div class="summary-section">
              <div class="summary-header">
                <span class="summary-title">Scheduled</span>
                <span class="summary-total">{{ getWeeklySummary(idx).scheduledTotal }}</span>
              </div>
              <div v-if="Object.keys(getWeeklySummary(idx).scheduledByCode).length > 0" class="summary-breakdown">
                <div v-for="(hours, code) in getWeeklySummary(idx).scheduledByCode" :key="'sched-' + code" class="breakdown-item">
                  <span class="code-label">{{ code }}:</span>
                  <span class="hours-value">{{ hours }}</span>
                </div>
              </div>
            </div>
            <div class="summary-section">
              <div class="summary-header">
                <span class="summary-title">Approved</span>
                <span class="summary-total">{{ getWeeklySummary(idx).approvedTotal }}</span>
              </div>
              <div v-if="Object.keys(getWeeklySummary(idx).approvedByCode).length > 0" class="summary-breakdown">
                <div v-for="(hours, code) in getWeeklySummary(idx).approvedByCode" :key="'app-' + code" class="breakdown-item">
                  <span class="code-label">{{ code }}:</span>
                  <span class="hours-value">{{ hours }}</span>
                </div>
              </div>
            </div>
            <div v-if="getWeeklySummary(idx).milesTotal !== null" class="summary-section">
              <div class="summary-header">
                <span class="summary-title">Miles</span>
                <span class="summary-total">{{ getWeeklySummary(idx).milesTotal.toFixed(1) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { isDSPDGroupShift, getCaregiverShiftStatus, getStatusIcon, formatCaregiverStatusHtml, caregiverNameWithRole } from '@/utils/shiftStatus';

export default {
  name: "MonthView",
  props: {
    currentMonth: { type: Date, required: true },
    daysInMonth: { type: Array, required: true },
    dayNames: { type: Array, required: true },
    eventsForDate: { type: Function, required: true },
    isToday: { type: Function, required: true },
    workSchedule: { type: Object, default: () => ({}) },
    getScheduledHours: { type: Function, default: null },
    userRole: { type: String, default: "Admin" },
    approvedTimeOff: { type: Array, default: () => [] },
    showWeeklySummary: { type: Boolean, default: false },
    isInCustomRange: { type: Function, default: () => true },
    pageType: { type: String, default: null }, // 'client' or 'caregiver'
  },
  methods: {
    isDateOnTimeOff(date) {
      if (!this.approvedTimeOff || this.approvedTimeOff.length === 0) {
        return null;
      }

      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format

      for (const timeOff of this.approvedTimeOff) {
        if (dateStr >= timeOff.start_date && dateStr <= timeOff.end_date) {
          return timeOff;
        }
      }

      return null;
    },
    isFullDayOff(date) {
      const timeOff = this.isDateOnTimeOff(date);
      if (!timeOff) return false;

      // It's a full day off if it's NOT a partial day
      return timeOff.request_type !== 'single_partial_day';
    },
    formatTimeOffDisplay(timeOff) {
      if (!timeOff) return '';

      // For partial day time off, show the time range
      if (timeOff.request_type === 'single_partial_day' && timeOff.start_time && timeOff.end_time) {
        const startTime = this.formatTime(timeOff.start_time);
        const endTime = this.formatTime(timeOff.end_time);
        return `Time Off: ${startTime} - ${endTime}`;
      }

      // For full day time off
      return 'Approved Time Off';
    },
    getTimeOffTooltip(timeOff) {
      if (!timeOff) return '';

      let tooltip = '';
      if (timeOff.request_type === 'single_partial_day') {
        tooltip = `Partial Day Time Off: ${this.formatTime(timeOff.start_time)} - ${this.formatTime(timeOff.end_time)}`;
      } else if (timeOff.request_type === 'single_full_day') {
        tooltip = 'Full Day Time Off';
      } else {
        tooltip = 'Approved Time Off';
      }

      if (timeOff.reason) {
        tooltip += `\nReason: ${timeOff.reason}`;
      }

      return tooltip;
    },
    formatTime(timeString) {
      if (!timeString) return '';

      // Handle HH:MM:SS format
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes}${ampm}`;
      }

      return timeString;
    },
    getPayerClass(shift) {
      if (this.userRole === "Caregiver") {
        return "";
      }
      const payer = shift.payer || "DSPD";
      return `payer-${payer.toLowerCase().replace(/\s+/g, "-")}`;
    },
    getShiftTypeClass(shift) {
      const shiftType = shift.shiftType || "1-on-1";
      return `shift-type-${shiftType.toLowerCase().replace(/\s+/g, "-")}`;
    },
    formatShiftTooltip(shift) {
      let tooltip = ``;

      // Show primary display times first (actual or scheduled)
      if (shift.hasClockIn || shift.hasClockOut) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;

        if (shift.hasClockOut) {
          tooltip += `Actual: ${displayStart} - ${displayEnd}`;
          // Calculate hours worked from actual times
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            const totalMin = this.calculateShiftMinutes(actualStart, actualEnd);
            tooltip += `\nHours Worked: ${this.minutesToHoursFormat(totalMin)}`;
          }
        } else {
          tooltip += `Actual: ${displayStart} - In Progress`;
        }

        // Show scheduled times as reference
        const scheduledStart = shift.formattedScheduledStartTime;
        const scheduledEnd = shift.formattedScheduledEndTime;
        tooltip += `\nScheduled: ${scheduledStart} - ${scheduledEnd}`;
        tooltip += `\nHours Scheduled: ${this.minutesToHoursFormat(this.calculateShiftMinutes(shift.scheduledStartTime, shift.scheduledEndTime))}`;
      } else {
        // Show only scheduled times when not clocked in
        const scheduledStart = shift.formattedStartTime;
        const scheduledEnd = shift.formattedEndTime;
        tooltip += `Scheduled: ${scheduledStart} - ${scheduledEnd}`;
        tooltip += `\nHours Scheduled: ${this.minutesToHoursFormat(this.calculateShiftMinutes(shift.startTime, shift.endTime))}`;
      }

      // Display group name once if it exists, then list individual names
      const clientDisplay = shift.clients && shift.clients.length > 1
        ? shift.clients.map(c => c.name).join(', ')
        : shift.client;
      let caregiverDisplay;
      if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        caregiverDisplay = shift.caregivers.map(caregiver => {
          const status = getCaregiverShiftStatus(shift.originalData, caregiver.id);
          const statusIcon = getStatusIcon(status);
          return `${statusIcon} ${caregiver.name}`;
        }).join(', ');
      } else {
        caregiverDisplay = shift.caregivers && shift.caregivers.length > 1
          ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ')
          : shift.caregiver;
      }

      const clientLabel = shift.clients && shift.clients.length > 1 ? 'Clients' : 'Client';
      const caregiverLabel = shift.caregivers && shift.caregivers.length > 1 ? 'Caregivers' : 'Caregiver';

      if (shift.groupName) {
        tooltip += `\nGroup: ${shift.groupName}`;
      }
      tooltip += `\n${clientLabel}: ${clientDisplay}`;
      tooltip += `\n${caregiverLabel}: ${caregiverDisplay}`;
      tooltip += `\nPayer: ${shift.payer}`;

      // Get service codes - check both service_codes and non_service_codes
      const serviceCodes = shift.originalData?.service_codes || [];
      const nonServiceCodes = shift.originalData?.non_service_codes || [];

      // Use non_service_codes if payer is Non Service, otherwise use service_codes
      const codesToDisplay = (shift.payer === 'Non Service' ? nonServiceCodes : serviceCodes);
      const serviceCodeList = codesToDisplay.map(sc => sc.code).filter(Boolean).join(', ');

      if (serviceCodeList) {
        tooltip += `\nCode: ${serviceCodeList}`;
      }

      tooltip += `\nShift Type: ${shift.shiftType}`;

      // Capitalize status
      const capitalizedStatus = shift.status ? shift.status.charAt(0).toUpperCase() + shift.status.slice(1) : '';
      tooltip += `\nStatus: ${capitalizedStatus}`;

      return tooltip;
    },
    isGroupShiftForTooltip(shift) {
      return isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead');
    },
    formatShiftTooltipHtml(shift) {
      let lines = [];
      const isGroup = isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead');

      if (!isGroup && (shift.hasClockIn || shift.hasClockOut)) {
        const displayStart = shift.formattedStartTime;
        const displayEnd = shift.formattedEndTime;
        if (shift.hasClockOut) {
          lines.push(`Actual: ${displayStart} - ${displayEnd}`);
          const actualStart = shift.actualStartTime || shift.formattedStartTime;
          const actualEnd = shift.actualEndTime || shift.formattedEndTime;
          if (actualStart && actualEnd) {
            lines.push(`Hours Worked: ${this.minutesToHoursFormat(this.calculateShiftMinutes(actualStart, actualEnd))}`);
          }
        } else {
          lines.push(`Actual: ${displayStart} - In Progress`);
        }
        lines.push(`Scheduled: ${shift.formattedScheduledStartTime} - ${shift.formattedScheduledEndTime}`);
        lines.push(`Hours Scheduled: ${this.minutesToHoursFormat(this.calculateShiftMinutes(shift.scheduledStartTime, shift.scheduledEndTime))}`);
      } else {
        const scheduledStart = isGroup ? shift.formattedScheduledStartTime || shift.formattedStartTime : shift.formattedStartTime;
        const scheduledEnd = isGroup ? shift.formattedScheduledEndTime || shift.formattedEndTime : shift.formattedEndTime;
        const startCalc = isGroup ? shift.scheduledStartTime || shift.startTime : shift.startTime;
        const endCalc = isGroup ? shift.scheduledEndTime || shift.endTime : shift.endTime;
        lines.push(`Scheduled: ${scheduledStart} - ${scheduledEnd}`);
        lines.push(`Hours Scheduled: ${this.minutesToHoursFormat(this.calculateShiftMinutes(startCalc, endCalc))}`);
      }

      const isMultipleClients = shift.clients && shift.clients.length > 1;
      const isMultipleCaregivers = shift.caregivers && shift.caregivers.length > 1;
      const showBothLines = isMultipleClients || isMultipleCaregivers;

      if (this.pageType !== 'client' || showBothLines) {
        const clientDisplay = isMultipleClients
          ? shift.clients.map(c => c.name).join(', ') : shift.client;
        const clientLabel = isMultipleClients ? 'Clients' : 'Client';
        if (shift.groupName) lines.push(`Group: ${shift.groupName}`);
        lines.push(`${clientLabel}: ${clientDisplay}`);
      }

      if (this.pageType !== 'caregiver' || showBothLines) {
        const caregiverLabel = isMultipleCaregivers ? 'Caregivers' : 'Caregiver';
        if (isDSPDGroupShift(shift.originalData) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
          const caregiverHtml = formatCaregiverStatusHtml(shift.originalData, shift.caregivers);
          lines.push(`${caregiverLabel}:<br>${caregiverHtml}`);
        } else {
          const caregiverDisplay = isMultipleCaregivers
            ? shift.caregivers.map(c => caregiverNameWithRole(c)).join(', ') : shift.client;
          lines.push(`${caregiverLabel}: ${caregiverDisplay}`);
        }
      }

      lines.push(`Payer: ${shift.payer}`);
      const serviceCodes = shift.originalData?.service_codes || [];
      const nonServiceCodes = shift.originalData?.non_service_codes || [];
      const codesToDisplay = (shift.payer === 'Non Service' ? nonServiceCodes : serviceCodes);
      const serviceCodeList = codesToDisplay.map(sc => sc.code).filter(Boolean).join(', ');
      if (serviceCodeList) lines.push(`Code: ${serviceCodeList}`);
      lines.push(`Shift Type: ${shift.shiftType}`);
      const capitalizedStatus = shift.status ? shift.status.charAt(0).toUpperCase() + shift.status.slice(1) : '';
      lines.push(`Status: ${capitalizedStatus}`);
      if (shift.miles != null) lines.push(`Miles: ${shift.miles.toFixed(1)}`);

      const cancelReason = shift.originalData?.cancel_reason;
      if (cancelReason) {
        lines.push(`<span style="color:rgb(180, 140, 100);">Canceled: ${cancelReason}</span>`);
      }

      const notes = shift.notes || shift.originalData?.shift_notes;
      if (notes) {
        lines.push(`Notes: ${notes.replace(/\n/g, '<br>')}`);
      }

      return lines.join('<br>');
    },
    getWeeklySummary(saturdayIdx) {
      // Get the week's worth of days (Sunday to Saturday)
      const weekStartIdx = saturdayIdx - 6;
      const weekDays = this.daysInMonth.slice(weekStartIdx, saturdayIdx + 1);

      const summary = {
        scheduledTotal: '0.00',
        scheduledByCode: {},
        approvedTotal: '0.00',
        approvedByCode: {},
        milesTotal: null
      };

      let scheduledMinutes = 0;
      let approvedMinutes = 0;
      let milesTotal = null;

      // Process each day in the week
      weekDays.forEach(date => {
        const shifts = this.eventsForDate(date);

        shifts.forEach(shift => {
          // Determine service code based on payer
          let serviceCode;
          const payer = shift.payer || shift.originalData?.payer || 'DSPD';

          if (payer === 'Non Service') {
            // Lump all Non Service shifts under "Non Service"
            serviceCode = 'Non Service';
          } else {
            // For other payers, get individual service codes
            const serviceCodes = shift.originalData?.service_codes || [];
            serviceCode = serviceCodes.length > 0 ? serviceCodes[0].code : 'No Code';
          }

          // Calculate hours for scheduled shifts (ALL shifts regardless of status)
          const minutes = this.calculateShiftMinutes(shift.startTime, shift.endTime);
          scheduledMinutes += minutes;

          if (!summary.scheduledByCode[serviceCode]) {
            summary.scheduledByCode[serviceCode] = 0;
          }
          summary.scheduledByCode[serviceCode] += minutes;

          // Calculate hours for approved shifts (ONLY approved)
          // Use ACTUAL worked hours (clock in/out times) instead of scheduled hours
          if (shift.status === 'approved') {
            // Get actual clock in/out times
            // For group shifts, clock_in/clock_out on the main shift are NULL;
            // the per-caregiver times are resolved in clockInRaw/clockOutRaw
            const actualStartTime = shift.clockInRaw ||
                                   shift.originalData?.clockIn ||
                                   shift.originalData?.clock_in ||
                                   shift.startTime;
            const actualEndTime = shift.clockOutRaw ||
                                 shift.originalData?.clockOut ||
                                 shift.originalData?.clock_out ||
                                 shift.endTime;

            // Calculate actual worked minutes, subtracting breaks
            let actualMinutes = this.calculateShiftMinutes(actualStartTime, actualEndTime);
            const breakMinutes = this.calculateBreakMinutes(shift.originalData?.breaks);
            actualMinutes = Math.max(0, actualMinutes - breakMinutes);

            if (!summary.approvedByCode[serviceCode]) {
              summary.approvedByCode[serviceCode] = 0;
            }
            summary.approvedByCode[serviceCode] += actualMinutes;
            approvedMinutes += actualMinutes;
          }

          // Recorded miles (null until at least one shift has a closed trip)
          if (shift.miles != null) {
            milesTotal = (milesTotal || 0) + shift.miles;
          }
        });
      });

      // Convert minutes to hours:minutes format
      summary.scheduledTotal = this.minutesToHoursFormat(scheduledMinutes);
      summary.approvedTotal = this.minutesToHoursFormat(approvedMinutes);
      summary.milesTotal = milesTotal;

      // Convert code breakdowns to hours:minutes format
      for (const code in summary.scheduledByCode) {
        summary.scheduledByCode[code] = this.minutesToHoursFormat(summary.scheduledByCode[code]);
      }
      for (const code in summary.approvedByCode) {
        summary.approvedByCode[code] = this.minutesToHoursFormat(summary.approvedByCode[code]);
      }

      return summary;
    },
    calculateShiftMinutes(startTime, endTime) {
      if (!startTime || !endTime) return 0;

      // Try to parse as full datetime strings first for millisecond precision
      const startDate = new Date(startTime);
      const endDate = new Date(endTime);

      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) &&
          (String(startTime).includes('T') || String(startTime).includes('-'))) {
        let diffMs = endDate - startDate;
        // Handle overnight shifts
        if (diffMs < 0) {
          diffMs += 24 * 60 * 60 * 1000;
        }
        return diffMs / (1000 * 60); // return fractional minutes
      }

      // Fallback: parse as HH:MM or HH:MM:SS time strings
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        const parts = String(timeStr).split(':').map(Number);
        if (parts.length < 2 || parts.some(isNaN)) return null;
        const [h, m, s = 0] = parts;
        return h * 60 + m + s / 60;
      };

      const startMinutes = parseTime(startTime);
      let endMinutes = parseTime(endTime);

      if (startMinutes === null || endMinutes === null) return 0;

      // Handle overnight shifts
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }

      return endMinutes - startMinutes;
    },
    calculateBreakMinutes(breaks) {
      if (!breaks || !Array.isArray(breaks) || breaks.length === 0) return 0;
      let totalBreakMinutes = 0;
      breaks.forEach(b => {
        if (b.break_start && b.break_end) {
          const breakStart = new Date(b.break_start);
          const breakEnd = new Date(b.break_end);
          if (!isNaN(breakStart.getTime()) && !isNaN(breakEnd.getTime())) {
            const diffMs = breakEnd - breakStart;
            if (diffMs > 0) {
              totalBreakMinutes += diffMs / (1000 * 60);
            }
          }
        }
      });
      return totalBreakMinutes;
    },
    minutesToHoursFormat(minutes) {
      const decimalHours = minutes / 60;
      return (Math.round(decimalHours * 100) / 100).toFixed(2);
    },
  },
};
</script>

<style scoped>
.custom-calendar {
  width: 100%;
}
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.calendar-grid.with-weekly-summary {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.day-name {
  font-weight: bold;
  text-align: center;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

.calendar-day {
  position: relative;
  background: transparent;
  border-right: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  min-height: 80px;
  padding: 4px;
  cursor: pointer;
}

/* Default 7-column layout */
.calendar-day:nth-child(7n) {
  border-right: none;
}
.calendar-day:nth-last-child(-n + 7) {
  border-bottom: none;
}
.day-name:nth-child(7n) {
  border-right: none;
}

/* 8-column layout when weekly summary is shown */
.calendar-grid.with-weekly-summary .calendar-day:nth-child(7n) {
  border-right: 1px solid #ccc;
}
.calendar-grid.with-weekly-summary .calendar-day:nth-child(8n) {
  border-right: none;
}
.calendar-grid.with-weekly-summary .calendar-day:nth-last-child(-n + 8) {
  border-bottom: none;
}
.calendar-grid.with-weekly-summary .day-name:nth-child(7n) {
  border-right: 1px solid #ccc;
}
.calendar-grid.with-weekly-summary .day-name:nth-child(8n) {
  border-right: none;
}
.date-number {
  font-size: 0.85rem;
  font-weight: bold;
}
.today {
  background-color: rgba(107, 166, 205, 0.2);
  border: 2px solid #6BA6CD;
}
.calendar-day.out-of-range {
  opacity: 0.4;
}

.calendar-day.out-of-range .shift-item {
  opacity: 0.6;
}
.shift-item {
  margin-top: 2px;
  font-size: 0.7rem;
  color: #fff;
  padding: 4px 6px;
  min-height: 24px;
  border-radius: 3px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
}

.shift-time {
  font-weight: bold;
  flex-shrink: 0;
}

.shift-client {
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.9;
}

.shift-caregiver {
  font-size: 0.6rem;
  color: #e0e0e0;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

/*noinspection CssUnusedSymbol */
.status-unassigned {
  background: linear-gradient(
    135deg,
    rgba(32, 201, 151, 0.7),
    rgba(32, 201, 151, 0.5)
  ); /* Teal for unassigned shifts */
}

/*noinspection CssUnusedSymbol */
.status-scheduled {
  background: linear-gradient(
    135deg,
    rgba(52, 144, 220, 0.7),
    rgba(52, 144, 220, 0.5)
  ); /* Muted blue */
}

/*noinspection CssUnusedSymbol */
.status-clocked-in {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ); /* Bright yellow for clocked in */
}

/*noinspection CssUnusedSymbol */
.status-clocked-out {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ); /* Muted orange */
}

/*noinspection CssUnusedSymbol */
.status-approved {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ); /* Muted deep purple */
}

/*noinspection CssUnusedSymbol */
.status-available {
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.7),
    rgba(76, 175, 80, 0.5)
  ); /* Muted light green */
}

/*noinspection CssUnusedSymbol */
.status-missed {
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  ); /* Grey for missed shifts */
}

/*noinspection CssUnusedSymbol */
.status-error {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.8),
    rgba(220, 53, 69, 0.6)
  ); /* Red for error shifts */
}

/* Shift type and payer colors are now defined in global shift-colors.css */

/* Approved Time Off Indicator */
.time-off-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  margin: 2px 0;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.6);
  border-radius: 3px;
  font-size: 0.65rem;
  color: #f57c00;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.time-off-indicator i {
  font-size: 0.6rem;
  opacity: 0.9;
  flex-shrink: 0;
}

.time-off-indicator span {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

/* Work Schedule Indicator */
.work-schedule-container {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.work-schedule-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  margin: 1px 0;
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.3);
  border-radius: 3px;
  font-size: 0.65rem;
  color: #6ba6cd;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.work-schedule-indicator i {
  font-size: 0.6rem;
  opacity: 0.8;
}

.work-schedule-indicator span {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Weekly Summary Styles */
.weekly-summary-header {
  background: rgba(76, 175, 80, 0.1);
  border-left: 2px solid rgba(76, 175, 80, 0.4);
  font-weight: bold;
  color: #4caf50;
}

.weekly-summary-cell {
  background: rgba(76, 175, 80, 0.05);
  border-left: 2px solid rgba(76, 175, 80, 0.3);
  cursor: default !important;
  padding: 8px;
}

.weekly-summary-cell:hover {
  background: rgba(76, 175, 80, 0.08);
}

.weekly-summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 0.75rem;
}

.summary-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
  padding-bottom: 3px;
}

.summary-title {
  font-weight: bold;
  color: #fff;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-total {
  font-size: 0.85rem;
  font-weight: bold;
  color: #4caf50;
}

.summary-breakdown {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
}

.breakdown-item {
  font-size: 0.65rem;
  color: #b3a6a6;
  display: flex;
  justify-content: space-between;
  padding: 1px 4px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 2px;
}

.code-label {
  font-weight: 600;
  color: #6ba6cd;
}

.hours-value {
  color: #b3a6a6;
}

@media (max-width: 768px) {
  .day-name {
    font-size: 0.7rem;
    padding: 4px 0;
    letter-spacing: 0.02em;
  }

  .calendar-day {
    min-height: 56px;
    padding: 2px;
  }

  .date-number {
    font-size: 0.75rem;
  }

  .shift-item {
    font-size: 0.6rem;
    padding: 2px 3px;
    min-height: 18px;
    gap: 2px;
  }

  /* Not enough room for a third line of text in ~50px-wide cells;
     caregiver still shows in the tooltip/details. */
  .shift-caregiver {
    display: none;
  }

  .time-off-indicator {
    font-size: 0.55rem;
    padding: 2px 3px;
  }

  .work-schedule-container {
    gap: 0;
  }

  .work-schedule-indicator {
    font-size: 0.55rem;
    padding: 1px 2px;
    margin: 0;
  }

  .work-schedule-indicator i {
    display: none;
  }

  .weekly-summary-content {
    font-size: 0.65rem;
    gap: 8px;
  }

  .summary-header {
    padding-bottom: 2px;
  }

  .summary-title {
    font-size: 0.6rem;
  }

  .summary-total {
    font-size: 0.7rem;
  }

  .breakdown-item {
    font-size: 0.55rem;
  }
}
</style>
